/**
 * Sentry `beforeBreadcrumb` hook that redacts sensitive query-string
 * parameters from HTTP/fetch breadcrumb URLs before the breadcrumb is
 * attached to an event. Provider connection tests and worker fetches
 * carry the credential in the query string (e.g.
 * `https://app.scrapingbee.com/api/v1/usage?api_key=<secret>`,
 * `https://api.scraperapi.com/account?api_key=<secret>`), and Sentry's
 * default HTTP/fetch instrumentation records the full URL in
 * `breadcrumb.data.url`. Without this hook that key leaks into Sentry.
 *
 * We redact in place across `breadcrumb.data.url`, any other string field
 * on `breadcrumb.data`, and `breadcrumb.message` (some integrations stash
 * the URL there). The structural input type keeps this framework-agnostic
 * so the same implementation works against `@sentry/nextjs` and
 * `@sentry/node` breadcrumb shapes; per-app wrappers re-narrow to their
 * local `Breadcrumb` type for compile-time safety.
 *
 * Console breadcrumbs also capture raw Prisma error messages (Next.js logs
 * uncaught request errors to the console, and the message carries the model,
 * method, and constraint/column names that `scrub-prisma-error.ts` strips
 * from the exception itself). Those are collapsed to the error class plus
 * the `P####` code when present.
 */
export type ScrubbableBreadcrumb = {
  message?: string
  data?: Record<string, unknown>
}

// Matches `?key=value` / `&key=value` for credential-bearing param names and
// replaces the value, preserving the leading delimiter + param name. Shared with
// the worker log sanitizer (`apps/worker/src/lib/logger-sanitizers.ts`).
export const SENSITIVE_QUERY_PARAM_RE =
  /([?&](?:access_token|refresh_token|api[_-]?key|apikey|token|key|secret|signature|sig|password|auth|code)=)([^&#\s]+)/gi

const REDACTED = "[Redacted]"

export function redactSensitiveQuery(value: string): string {
  return value.replace(SENSITIVE_QUERY_PARAM_RE, `$1${REDACTED}`)
}

const PRISMA_ERROR_CLASS_RE = /\bPrismaClient\w*Error\b/

export function redactPrismaMessage(value: string): string {
  const errorClass = value.match(PRISMA_ERROR_CLASS_RE)?.[0]
  if (!errorClass) return value
  const code = value.match(/\bP\d{4}\b/)?.[0]
  return `${errorClass}: Prisma error ${code ?? "(scrubbed)"}`
}

function redactBreadcrumbString(value: string): string {
  return redactPrismaMessage(redactSensitiveQuery(value))
}

export function scrubSentryBreadcrumb<B extends ScrubbableBreadcrumb | null>(breadcrumb: B): B {
  if (breadcrumb == null) return breadcrumb
  if (typeof breadcrumb.message === "string") {
    breadcrumb.message = redactBreadcrumbString(breadcrumb.message)
  }
  const data = breadcrumb.data
  if (data && typeof data === "object") {
    for (const [key, val] of Object.entries(data)) {
      if (typeof val === "string") data[key] = redactBreadcrumbString(val)
    }
  }
  return breadcrumb
}
